import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  qs,
  type Allocation,
  type Consultant,
  type ExecutionDetail,
  type ExecutionRow,
  type ExecutionStatus,
  type Lookup,
  type Paged,
  type ProjectDetail,
  type ServiceModality,
  type SourceKind,
  type SummaryRun,
} from '../api';
import { useMe } from '../auth';
import { Dialog, Empty, ErrorAlert, Field, HowTo, Loading, useOnline } from '../components/ui';
import { ProjectPicker } from '../components/ProjectPicker';
import { PrepareMessageButton, RelatedMessages } from './Mensagens';
import { getDraft, resolveConflict, removeDraft, saveDraft, syncDrafts, useLocalDrafts, type LocalDraft } from '../offline';
import {
  addDays,
  date,
  dateTime,
  EXECUTION_STATUS_LABEL,
  hm,
  localParts,
  MODALITY_LABEL,
  money,
  parseHoursInput,
  SERVICE_MODALITY_LABEL,
  todayIso,
  zonedIso,
} from '../format';

const STATUS_CLASS: Record<ExecutionStatus, string> = { rascunho: '', pronto: 'badge-brand', confirmado: 'badge-ok', anulado: 'badge-off' };
export const ExecStatus = ({ status }: { status: ExecutionStatus }) => <span className={`badge ${STATUS_CLASS[status]}`}>{EXECUTION_STATUS_LABEL[status]}</span>;

const SYNC_LABEL = { pendente: 'Só neste aparelho', sincronizado: 'Sincronizado', conflito: 'Conflito', erro: 'Erro ao enviar' } as const;
const SYNC_CLASS = { pendente: 'badge-demo', sincronizado: 'badge-ok', conflito: 'badge-demo', erro: 'badge-demo' } as const;

// ------------------------------------------------------------------ Meus registros

export function ExecutionsPage() {
  const me = useMe();
  const online = useOnline();
  const drafts = useLocalDrafts(me.id);
  const [status, setStatus] = useState<'abertas' | 'confirmado' | 'all'>('abertas');
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const q = useQuery({
    queryKey: ['executions', status, scope],
    queryFn: () => api.get<Paged<ExecutionRow>>(`/executions${qs({ status, scope, pageSize: 50 })}`),
    enabled: online,
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Execuções</h1>
          <p>Rascunhos, conferência e execuções confirmadas.</p>
        </div>
        <div className="actions">
          <Link className="btn btn-primary" to="/execucoes/nova">
            Nova execução
          </Link>
        </div>
      </div>
      <HowTo id="execucoes" title="Como registrar um atendimento">
        <ol>
          <li><strong>Estava na agenda?</strong> Abra o compromisso › <strong>Foi realizado — registrar atendimento</strong>. Já vem preenchido.</li>
          <li><strong>Não estava?</strong> <strong>Nova execução</strong> › projeto, o que foi feito, duração › <strong>Salvar rascunho</strong> (funciona sem internet).</li>
          <li>Abra o registro, complete o que o quadro amarelo pedir e clique em <strong>Salvar e confirmar</strong>.</li>
        </ol>
        <p className="small muted" style={{ marginTop: '0.4rem' }}>Ao confirmar: banco de horas desconta o saldo; hora avulsa gera cobrança no Faturamento; mensalidade e valor fechado só registram. Nada é enviado ao cliente.</p>
      </HowTo>

      {drafts.length > 0 && (
        <section className="card">
          <div className="card-head">
            <h2>Rascunhos neste aparelho</h2>
            <button className="btn btn-sm" disabled={!online} onClick={() => void syncDrafts(me.id)}>
              Sincronizar agora
            </button>
          </div>
          <p className="small muted">
            Rascunhos “só neste aparelho” ainda não chegaram ao servidor. Limpar os dados do navegador apaga esses rascunhos.
          </p>
          <div className="list">
            {drafts.map((d) => (
              <LocalDraftItem key={d.clientDraftId} d={d} userId={me.id} />
            ))}
          </div>
        </section>
      )}

      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Situação">
          <button aria-pressed={status === 'abertas'} onClick={() => setStatus('abertas')}>
            A completar
          </button>
          <button aria-pressed={status === 'confirmado'} onClick={() => setStatus('confirmado')}>
            Confirmadas
          </button>
          <button aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
            Todas
          </button>
        </div>
        <div className="segmented" role="group" aria-label="Escopo">
          <button aria-pressed={scope === 'mine'} onClick={() => setScope('mine')}>
            Meus registros
          </button>
          <button aria-pressed={scope === 'all'} onClick={() => setScope('all')}>
            Toda a TECHNOVE
          </button>
        </div>
      </div>
      {!online && <div className="alert alert-warning">Sem conexão: a lista do servidor não pode ser atualizada. Você ainda pode criar rascunhos rápidos.</div>}
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : q.data?.data.length === 0 ? (
        <Empty>Nenhuma execução nesta lista.</Empty>
      ) : (
        <div className="list">
          {q.data?.data.map((e) => (
            <Link key={e.id} to={`/execucoes/${e.id}`} className="list-item">
              <div className="title">
                {e.projectName ?? 'Pendente de vínculo'} <ExecStatus status={e.status} />
              </div>
              <div className="meta">
                {e.clientName && <span>{e.clientName}</span>}
                <span>{date(e.serviceDate)}</span>
                <span>{hm(e.durationMinutes)}</span>
                <span>Executor: {e.executorName}</span>
              </div>
              {(e.summary || e.quickNote) && <div className="small muted" style={{ marginTop: 4 }}>{(e.summary || e.quickNote)!.slice(0, 140)}</div>}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function LocalDraftItem({ d, userId }: { d: LocalDraft; userId: string }) {
  return (
    <div className="list-item">
      <div className="title">
        {d.data.projectLabel ?? 'Pendente de vínculo'} <span className={`badge ${SYNC_CLASS[d.syncState]}`}>{SYNC_LABEL[d.syncState]}</span>
      </div>
      <div className="meta">
        <span>{date(d.data.serviceDate)}</span>
        {d.data.durationMinutes != null && <span>{hm(d.data.durationMinutes)}</span>}
        <span>Alterado em {dateTime(d.updatedAt)}</span>
      </div>
      <div className="small" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
        {d.data.quickNote}
      </div>
      {d.message && <div className="small" style={{ color: 'var(--danger)', marginTop: 4 }}>{d.message}</div>}
      {d.syncState === 'conflito' && d.serverCopy && (
        <div className="grid grid-2" style={{ marginTop: 8 }}>
          <div className="pending-block small">
            <strong>No aparelho</strong>
            <div style={{ whiteSpace: 'pre-wrap' }}>{d.data.quickNote}</div>
          </div>
          <div className="pending-block small">
            <strong>No servidor (versão {d.serverCopy.version})</strong>
            <div style={{ whiteSpace: 'pre-wrap' }}>{d.serverCopy.quickNote}</div>
          </div>
        </div>
      )}
      <div className="actions" style={{ marginTop: 8 }}>
        {d.syncState === 'conflito' && d.serverCopy && (
          <>
            <button className="btn btn-sm" onClick={() => void resolveConflict(userId, d, 'aparelho')}>
              Manter a do aparelho
            </button>
            <button className="btn btn-sm" onClick={() => void resolveConflict(userId, d, 'servidor')}>
              Manter a do servidor
            </button>
          </>
        )}
        {d.syncState !== 'conflito' && (
          <Link className="btn btn-sm" to={`/execucoes/rascunho/${d.clientDraftId}`}>
            Editar no aparelho
          </Link>
        )}
        {d.serverId && (
          <Link className="btn btn-sm btn-primary" to={`/execucoes/${d.serverId}`}>
            Completar
          </Link>
        )}
        {(d.syncState === 'sincronizado' || (d.syncState === 'conflito' && !d.serverCopy)) && (
          <button className="btn btn-sm btn-ghost" onClick={() => void removeDraft(userId, d.clientDraftId)}>
            Remover do aparelho
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ rascunho rápido (funciona sem conexão)

export function QuickDraftPage() {
  const me = useMe();
  const online = useOnline();
  const navigate = useNavigate();
  const { clientDraftId } = useParams();
  const [draft, setDraft] = useState<LocalDraft | null>(null);
  const [form, setForm] = useState({ projectId: null as string | null, projectLabel: null as string | null, quickNote: '', serviceDate: todayIso(), duration: '', modality: '' });
  const [saved, setSaved] = useState<LocalDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientDraftId) return;
    void getDraft(me.id, clientDraftId).then((d) => {
      if (!d) return;
      setDraft(d);
      setForm({
        projectId: d.data.projectId,
        projectLabel: d.data.projectLabel,
        quickNote: d.data.quickNote,
        serviceDate: d.data.serviceDate ?? todayIso(),
        duration: d.data.durationMinutes != null ? hm(d.data.durationMinutes) : '',
        modality: d.data.modality ?? '',
      });
    });
  }, [clientDraftId, me.id]);

  // O rascunho é salvo primeiro no aparelho e enviado ao servidor quando houver conexão (seção 8.2).
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const durationMinutes = form.duration.trim() ? parseHoursInput(form.duration) : null;
    if (form.duration.trim() && !durationMinutes) return setError('Duração inválida. Use, por exemplo, 1h30 ou 1,5.');
    const local: LocalDraft = {
      clientDraftId: draft?.clientDraftId ?? crypto.randomUUID(),
      data: {
        projectId: form.projectId,
        projectLabel: form.projectLabel,
        quickNote: form.quickNote,
        serviceDate: form.serviceDate || null,
        durationMinutes,
        modality: (form.modality || null) as ServiceModality | null,
      },
      baseVersion: draft?.baseVersion ?? null,
      serverId: draft?.serverId ?? null,
      syncState: 'pendente',
      message: null,
      serverCopy: null,
      updatedAt: new Date().toISOString(),
    };
    try {
      await saveDraft(me.id, local);
    } catch {
      return setError('Não foi possível salvar no aparelho (armazenamento do navegador indisponível).');
    }
    await syncDrafts(me.id);
    setSaved((await getDraft(me.id, local.clientDraftId)) ?? local);
    setDraft((await getDraft(me.id, local.clientDraftId)) ?? local);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{clientDraftId ? 'Editar rascunho' : 'Nova execução — rascunho rápido'}</h1>
          <p>Anote agora e complete depois. Funciona sem internet.</p>
        </div>
      </div>
      {!online && <div className="alert alert-warning">Sem conexão. O rascunho fica guardado neste aparelho e será enviado automaticamente quando a conexão voltar.</div>}
      {saved && (
        <div className={`alert ${saved.syncState === 'sincronizado' ? 'alert-success' : 'alert-warning'}`} role="status">
          <strong>{saved.syncState === 'sincronizado' ? 'Rascunho salvo e sincronizado.' : 'Rascunho salvo só neste aparelho.'}</strong>
          {saved.syncState === 'sincronizado'
            ? 'Ele já aparece no computador. Nada foi confirmado nem consumido.'
            : saved.message ?? 'Será enviado quando houver conexão.'}
          <div className="actions" style={{ marginTop: 8 }}>
            {saved.serverId && (
              <button className="btn btn-sm btn-primary" onClick={() => navigate(`/execucoes/${saved.serverId}`)}>
                Completar agora
              </button>
            )}
            <button className="btn btn-sm" onClick={() => navigate('/execucoes')}>
              Meus registros
            </button>
          </div>
        </div>
      )}
      <form className="card form" onSubmit={submit} noValidate style={{ maxWidth: 640 }}>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <Field label="Projeto" hint="Se ainda não souber, deixe em branco: o vínculo será exigido antes da confirmação.">
          {(p) => (
            <ProjectPicker
              id={p.id}
              value={form.projectId}
              allowEmpty="Ainda não sei (vincular depois)"
              onChange={(pid, proj) => setForm((f) => ({ ...f, projectId: pid, projectLabel: proj ? `${proj.name} — ${proj.clientTradeName || proj.clientName}` : null }))}
            />
          )}
        </Field>
        <Field label="O que foi feito" required>
          {(p) => <textarea {...p} value={form.quickNote} onChange={(e) => setForm((f) => ({ ...f, quickNote: e.target.value }))} rows={5} />}
        </Field>
        <div className="form-row">
          <Field label="Data">{(p) => <input {...p} type="date" value={form.serviceDate} onChange={(e) => setForm((f) => ({ ...f, serviceDate: e.target.value }))} />}</Field>
          <Field label="Duração (opcional)" hint="Ex.: 1h30 ou 1,5">
            {(p) => <input {...p} inputMode="decimal" value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))} />}
          </Field>
        </div>
        <Field label="Modalidade">
          {(p) => (
            <select {...p} value={form.modality} onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value }))}>
              <option value="">—</option>
              <option value="remoto">Remoto</option>
              <option value="presencial">Presencial</option>
            </select>
          )}
        </Field>
        <p className="small muted">Executor: {me.consultantName}. Salvar o rascunho não consome horas, não gera cobrança e não envia mensagens.</p>
        <button className="btn btn-primary" disabled={!form.quickNote.trim()}>
          Salvar rascunho
        </button>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ registro completo

interface AllocRow {
  categoryId: string;
  areaId: string;
  time: string;
}

export function ExecutionEditPage() {
  const { id } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['execution', id], queryFn: () => api.get<ExecutionDetail>(`/executions/${id}`) });
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.get<{ data: Lookup[] }>('/categories') });
  const areas = useQuery({ queryKey: ['areas'], queryFn: () => api.get<{ data: Lookup[] }>('/areas') });

  const [form, setForm] = useState({
    projectId: null as string | null,
    contractId: '',
    executorConsultantId: me.consultantId,
    method: 'horarios' as 'horarios' | 'informada',
    day: todayIso(),
    start: '',
    end: '',
    breakMinutes: 0,
    duration: '',
    modality: '' as ServiceModality | '',
    summary: '',
    quickNote: '',
  });
  const [allocs, setAllocs] = useState<AllocRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [saved, setSaved] = useState(false);

  const project = useQuery({
    queryKey: ['project', form.projectId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${form.projectId}`),
    enabled: Boolean(form.projectId),
  });

  useEffect(() => {
    const e = q.data;
    if (!e || dirty) return;
    const s = e.startedAt ? localParts(e.startedAt) : null;
    setForm({
      projectId: e.projectId,
      contractId: e.contractId ?? '',
      executorConsultantId: e.executorConsultantId,
      method: e.durationMethod === 'informada' || (!e.startedAt && e.durationMinutes) ? 'informada' : 'horarios',
      day: s?.date ?? e.serviceDate ?? todayIso(),
      start: s?.time ?? '',
      end: e.endedAt ? localParts(e.endedAt).time : '',
      breakMinutes: e.breakMinutes,
      duration: e.durationMethod === 'informada' && e.durationMinutes ? hm(e.durationMinutes) : '',
      modality: e.modality ?? '',
      summary: e.summary ?? '',
      quickNote: e.quickNote ?? '',
    });
    const rows = e.allocations.map((a) => ({ categoryId: a.categoryId, areaId: a.areaId ?? '', time: hm(a.minutes) }));
    // Simplificação: em rascunho, já deixa uma linha de categoria pronta com a duração total.
    if (rows.length === 0 && e.status === 'rascunho') rows.push({ categoryId: '', areaId: '', time: e.durationMinutes ? hm(e.durationMinutes) : '' });
    setAllocs(rows);
    // Simplificação: a anotação do rascunho rápido vira a descrição (o consultor revisa antes de confirmar).
    if (e.status === 'rascunho' && !e.summary && e.quickNote) {
      setForm((f) => ({ ...f, summary: e.quickNote ?? '' }));
      setDirty(true);
    }
  }, [q.data, dirty]);

  // Simplificação: projeto com um único contrato ativo → contrato escolhido sozinho.
  useEffect(() => {
    if (!q.data || q.data.status !== 'rascunho' || form.contractId || !form.projectId || !project.data) return;
    if (project.data.id !== form.projectId) return;
    const active = project.data.contracts.filter((c) => c.status === 'ativo');
    if (active.length === 1) {
      setDirty(true);
      setForm((f) => ({ ...f, contractId: active[0]!.id }));
    }
  }, [project.data, form.projectId, form.contractId, q.data]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setDirty(true);
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Duração prevista a partir do formulário (o servidor recalcula e valida).
  const preview = useMemo(() => {
    if (form.method === 'informada') return parseHoursInput(form.duration);
    if (!form.start || !form.end) return null;
    const startIso = zonedIso(form.day, form.start);
    const endIso = zonedIso(form.end <= form.start ? addDays(form.day, 1) : form.day, form.end);
    return Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60000) - form.breakMinutes;
  }, [form]);
  const allocTotal = allocs.reduce((s, a) => s + (parseHoursInput(a.time) ?? 0), 0);
  // Simplificação: com uma só categoria, o tempo dela acompanha a duração automaticamente.
  useEffect(() => {
    if (!q.data || (q.data.status !== 'rascunho' && q.data.status !== 'pronto')) return;
    if (allocs.length !== 1 || preview == null || preview <= 0) return;
    if (parseHoursInput(allocs[0]!.time) === preview) return;
    setAllocs((xs) => (xs.length === 1 ? [{ ...xs[0]!, time: hm(preview) }] : xs));
  }, [preview, allocs, q.data]);

  function buildBody() {
    const allocations: Allocation[] = allocs
      .filter((a) => a.categoryId)
      .map((a) => {
        const minutes = parseHoursInput(a.time);
        if (!minutes) throw new Error('Informe o tempo de cada categoria (ex.: 1h30).');
        return { categoryId: a.categoryId, areaId: a.areaId || null, minutes };
      });
    const times =
      form.method === 'horarios'
        ? form.start && form.end
          ? {
              startedAt: zonedIso(form.day, form.start),
              endedAt: zonedIso(form.end <= form.start ? addDays(form.day, 1) : form.day, form.end),
              breakMinutes: form.breakMinutes,
              serviceDate: form.day,
            }
          : { serviceDate: form.day }
        : (() => {
            const d = parseHoursInput(form.duration);
            if (form.duration && !d) throw new Error('Duração inválida. Use, por exemplo, 1h30.');
            return { durationMinutes: d, serviceDate: form.day };
          })();
    return {
      appointmentId: q.data!.appointmentId,
      projectId: form.projectId,
      clientId: form.projectId ? undefined : q.data!.clientId,
      contractId: form.contractId || null,
      executorConsultantId: form.executorConsultantId,
      modality: form.modality || null,
      summary: form.summary,
      quickNote: form.quickNote,
      allocations,
      ...times,
    };
  }

  const save = useMutation({
    mutationFn: () => api.put(`/executions/${id}`, { ...buildBody(), version: q.data!.version }),
    onSuccess: async () => {
      setDirty(false);
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['execution', id] });
      void qc.invalidateQueries({ queryKey: ['executions'] });
    },
  });
  const [sharedSession, setSharedSession] = useState<{ others: { executor: string }[] } | null>(null);
  const transition = useMutation({
    mutationFn: (args: 'rascunho' | 'pronto' | 'confirmado' | { to: 'confirmado'; sharedSessionCoverage: 'consumir' | 'somente_producao' }) =>
      api.post(`/executions/${id}/transition`, { ...(typeof args === 'string' ? { to: args } : args), version: q.data!.version }),
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'shared_session_coverage_required') setSharedSession(err.details as { others: { executor: string }[] });
    },
    onSuccess: () => {
      setSharedSession(null);
      setConfirmDialog(false);
      void qc.invalidateQueries({ queryKey: ['execution', id] });
      void qc.invalidateQueries({ queryKey: ['executions'] });
      void qc.invalidateQueries({ queryKey: ['me-overview'] });
    },
  });
  const voidM = useMutation({
    mutationFn: (reason: string) => api.post(`/executions/${id}/void`, { reason, version: q.data!.version }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['execution', id] }),
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const e = q.data!;
  const readOnly = e.status === 'anulado';
  const executorName = consultants.data?.data.find((c) => c.id === form.executorConsultantId)?.name;
  const contracts = project.data?.contracts.filter((c) => c.status === 'ativo' || c.id === form.contractId) ?? [];

  function submit(ev: FormEvent) {
    ev.preventDefault();
    setLocalError(null);
    try {
      buildBody();
    } catch (err) {
      return setLocalError((err as Error).message);
    }
    save.mutate();
  }

  // O que falta para confirmar, em linguagem simples (mesmas exigências do servidor).
  const canConfirm = e.status === 'rascunho' || e.status === 'pronto';
  const checklist: { ok: boolean; label: string }[] = [
    { ok: Boolean(form.projectId), label: 'Projeto escolhido' },
    { ok: Boolean(form.contractId), label: 'Contrato / cobertura escolhido' },
    { ok: Boolean(form.modality), label: 'Modalidade (remoto ou presencial)' },
    { ok: preview != null && preview > 0, label: 'Duração (horários ou duração informada)' },
    { ok: allocs.some((a) => a.categoryId), label: 'Categoria do atendimento' },
    {
      // Só cobra o tempo depois que há categoria escolhida (evita duas pendências pelo mesmo motivo).
      ok: !allocs.some((a) => a.categoryId) || (preview != null && allocs.filter((a) => a.categoryId).reduce((s, a) => s + (parseHoursInput(a.time) ?? 0), 0) === preview),
      label: 'Tempo da categoria igual à duração',
    },
    { ok: form.summary.trim().length > 0, label: 'Descrição do conteúdo (vai para o cliente)' },
  ];
  const missing = checklist.filter((c) => !c.ok);

  async function saveAndConfirm() {
    setLocalError(null);
    if (missing.length) return setLocalError(`Falta preencher: ${missing.map((m) => m.label.toLowerCase()).join('; ')}.`);
    if (dirty) {
      try {
        buildBody();
      } catch (err) {
        return setLocalError((err as Error).message);
      }
      try {
        await save.mutateAsync();
        await qc.refetchQueries({ queryKey: ['execution', id] });
      } catch {
        return; // o erro aparece no formulário
      }
    }
    transition.reset();
    setConfirmDialog(true);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/execucoes">← Execuções</Link>
            {e.appointmentId && (
              <>
                {' · '}
                <Link to={`/agenda/${e.appointmentId}`}>compromisso de origem</Link>
              </>
            )}
          </p>
          <h1>
            Execução <ExecStatus status={e.status} />
          </h1>
          <p>
            Executor: <strong>{e.executorName}</strong> · registrado por {e.authorName}
            {e.projectOwnerName && e.projectOwnerId !== e.executorConsultantId && <> · responsável habitual do projeto: {e.projectOwnerName}</>}
          </p>
        </div>
        {!readOnly && (
          <div className="actions">
            {canConfirm && (
              <button className="btn btn-primary" disabled={save.isPending || transition.isPending} onClick={() => void saveAndConfirm()}>
                {dirty ? 'Salvar e confirmar' : 'Conferir e confirmar'}
              </button>
            )}
            {e.status === 'rascunho' && (
              <button className="btn btn-ghost" disabled={dirty || transition.isPending} title={dirty ? 'Salve antes' : 'Opcional: deixa marcado para outra pessoa conferir'} onClick={() => transition.mutate('pronto')}>
                Pronto para conferência
              </button>
            )}
            {e.status === 'pronto' && (
              <button className="btn btn-ghost" disabled={transition.isPending} onClick={() => transition.mutate('rascunho')}>
                Voltar a rascunho
              </button>
            )}
          </div>
        )}
      </div>

      {e.status === 'anulado' && <div className="alert alert-info">Execução anulada. Motivo: {e.voidReason}</div>}
      {e.status === 'confirmado' && <CoveragePanel execution={e} />}
      <ErrorAlert error={confirmDialog ? null : transition.error} title="Ainda não é possível prosseguir." />
      {e.quickNote && e.status === 'rascunho' && !form.summary && (
        <div className="alert alert-info">
          <strong>Anotação do rascunho</strong>
          <span style={{ whiteSpace: 'pre-wrap' }}>{e.quickNote}</span>
        </div>
      )}

      {canConfirm && (
        <div className={`card checklist ${missing.length ? '' : 'checklist-ok'}`} aria-label="O que falta para confirmar">
          <h2>{missing.length ? `Para confirmar, falta${missing.length > 1 ? 'm' : ''} ${missing.length} ${missing.length > 1 ? 'itens' : 'item'}` : 'Tudo preenchido — pode confirmar'}</h2>
          <ul>
            {checklist.map((c) => (
              <li key={c.label} className={c.ok ? 'ok' : 'todo'}>
                <span aria-hidden="true">{c.ok ? '✓' : '○'}</span> {c.label}
              </li>
            ))}
          </ul>
          {!missing.length && (
            <p className="small muted">
              Clique em <strong>{dirty ? 'Salvar e confirmar' : 'Conferir e confirmar'}</strong> no topo. Nada é enviado ao cliente.
            </p>
          )}
        </div>
      )}

      <form className="card form" onSubmit={submit} noValidate>
        <h2>Registro</h2>
        {localError && <div className="alert alert-error" role="alert">{localError}</div>}
        <ErrorAlert error={save.error} />
        {saved && <div className="alert alert-success">Alterações salvas.</div>}
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, display: 'grid', gap: '1rem' }}>
          <div className="form-row">
            <Field label="Projeto" required hint={e.clientName ? `Cliente: ${e.clientName}` : 'Obrigatório para confirmar.'}>
              {(p) => <ProjectPicker id={p.id} value={form.projectId} allowEmpty="Pendente de vínculo" onChange={(pid) => {
                    setDirty(true);
                    setForm((f) => ({ ...f, projectId: pid, contractId: '' }));
                  }} />}
            </Field>
            <Field label="Contrato / cobertura" required hint="Define se consome banco, gera cobrança, mensalidade ou valor fechado.">
              {(p) => (
                <select {...p} value={form.contractId} disabled={!form.projectId} onChange={(ev) => set('contractId', ev.target.value)}>
                  <option value="">{form.projectId ? 'Selecione…' : 'Escolha o projeto primeiro'}</option>
                  {contracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {MODALITY_LABEL[c.modality]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          <div className="form-row">
            <Field
              label="Executor"
              hint={project.data && form.executorConsultantId !== project.data.ownerConsultantId ? `Substituição: o responsável habitual continua ${project.data.ownerName}.` : undefined}
            >
              {(p) => (
                <select {...p} value={form.executorConsultantId} onChange={(ev) => set('executorConsultantId', ev.target.value)}>
                  {consultants.data?.data
                    .filter((c) => c.active || c.id === form.executorConsultantId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.id === me.consultantId ? ' (você)' : ''}
                      </option>
                    ))}
                </select>
              )}
            </Field>
            <Field label="Modalidade" required>
              {(p) => (
                <select {...p} value={form.modality} onChange={(ev) => set('modality', ev.target.value as ServiceModality)}>
                  <option value="">—</option>
                  <option value="remoto">Remoto</option>
                  <option value="presencial">Presencial</option>
                </select>
              )}
            </Field>
          </div>

          <div className="segmented" role="group" aria-label="Forma de informar a duração">
            <button type="button" aria-pressed={form.method === 'horarios'} onClick={() => set('method', 'horarios')}>
              Horários reais
            </button>
            <button type="button" aria-pressed={form.method === 'informada'} onClick={() => set('method', 'informada')}>
              Duração informada
            </button>
          </div>
          <div className="form-row">
            <Field label="Data do serviço" required>
              {(p) => <input {...p} type="date" value={form.day} onChange={(ev) => set('day', ev.target.value)} />}
            </Field>
            {form.method === 'horarios' ? (
              <div className="form-row">
                <Field label="Início">{(p) => <input {...p} type="time" value={form.start} onChange={(ev) => set('start', ev.target.value)} />}</Field>
                <Field label="Fim" hint={form.start && form.end && form.end <= form.start ? 'Termina no dia seguinte.' : undefined}>
                  {(p) => <input {...p} type="time" value={form.end} onChange={(ev) => set('end', ev.target.value)} />}
                </Field>
              </div>
            ) : (
              <Field label="Duração" hint="Ex.: 1h30 ou 1,5 (sem horários)">
                {(p) => <input {...p} inputMode="decimal" value={form.duration} onChange={(ev) => set('duration', ev.target.value)} />}
              </Field>
            )}
          </div>
          {form.method === 'horarios' && (
            <Field label="Intervalo (min)">
              {(p) => <input {...p} type="number" min={0} inputMode="numeric" value={form.breakMinutes} onChange={(ev) => set('breakMinutes', Number(ev.target.value) || 0)} style={{ maxWidth: 200 }} />}
            </Field>
          )}
          <div className={`alert ${preview != null && preview <= 0 ? 'alert-error' : 'alert-info'}`} role="status">
            Duração: <strong>{preview != null ? hm(preview) : '—'}</strong>
            {preview != null && preview > 0 && <> ({(preview / 60).toLocaleString('pt-BR', { maximumFractionDigits: 4 })} h para preço)</>}
            {preview != null && preview <= 0 && ' — duração inválida.'}
          </div>

          <fieldset>
            <legend>Categorias e módulos (rateio)</legend>
            <div className="grid" style={{ gap: '0.5rem' }}>
              {allocs.map((a, i) => (
                <div key={i} className="alloc-row">
                  <select aria-label="Categoria" value={a.categoryId} onChange={(ev) => (setDirty(true), setAllocs((xs) => xs.map((x, j) => (j === i ? { ...x, categoryId: ev.target.value } : x))))}>
                    <option value="">Categoria…</option>
                    {categories.data?.data
                      .filter((c) => c.active || c.id === a.categoryId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                  <select aria-label="Módulo/área" value={a.areaId} onChange={(ev) => (setDirty(true), setAllocs((xs) => xs.map((x, j) => (j === i ? { ...x, areaId: ev.target.value } : x))))}>
                    <option value="">Módulo/área…</option>
                    {areas.data?.data
                      .filter((c) => c.active || c.id === a.areaId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                  <input aria-label="Tempo" inputMode="decimal" placeholder="1h30" value={a.time} onChange={(ev) => (setDirty(true), setAllocs((xs) => xs.map((x, j) => (j === i ? { ...x, time: ev.target.value } : x))))} />
                  <button type="button" className="btn btn-ghost btn-sm" aria-label="Remover linha" onClick={() => (setDirty(true), setAllocs((xs) => xs.filter((_, j) => j !== i)))}>
                    ✕
                  </button>
                </div>
              ))}
              <div className="actions">
                <button type="button" className="btn btn-sm" onClick={() => (setDirty(true), setAllocs((xs) => [...xs, { categoryId: '', areaId: '', time: xs.length === 0 && preview ? hm(preview) : '' }]))}>
                  Adicionar categoria
                </button>
                {allocs.length === 1 && preview != null && preview > 0 && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => (setDirty(true), setAllocs((xs) => [{ ...xs[0]!, time: hm(preview) }]))}>
                    Usar a duração total
                  </button>
                )}
              </div>
              <span className={`small ${preview != null && allocs.length > 0 && allocTotal !== preview ? '' : 'muted'}`} style={preview != null && allocs.length > 0 && allocTotal !== preview ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
                Rateio: {hm(allocTotal)} de {preview != null ? hm(preview) : '—'}
                {preview != null && allocs.length > 0 && allocTotal !== preview && ' — a soma precisa igualar a duração.'}
              </span>
            </div>
          </fieldset>

          <Field label="Descrição do conteúdo" required hint="Vai para o relatório ao cliente. Pode ser preenchida à mão ou com o resumo assistido abaixo.">
            {(p) => <textarea {...p} rows={6} value={form.summary} onChange={(ev) => set('summary', ev.target.value)} />}
          </Field>
          {e.summaryOrigin === 'ia_revisada' && !dirty && <span className="small muted">Resumo aplicado a partir de sugestão revisada.</span>}
          <Field label="Anotação interna" hint="Não vai para o cliente.">
            {(p) => <textarea {...p} rows={2} value={form.quickNote} onChange={(ev) => set('quickNote', ev.target.value)} />}
          </Field>
          <div className="actions">
            {canConfirm && (
              <button type="button" className="btn btn-primary" disabled={save.isPending || transition.isPending} onClick={() => void saveAndConfirm()}>
                {dirty ? 'Salvar e confirmar' : 'Conferir e confirmar'}
              </button>
            )}
            <button className={canConfirm ? 'btn' : 'btn btn-primary'} disabled={save.isPending || !dirty}>
              {save.isPending ? 'Salvando…' : canConfirm ? 'Só salvar' : 'Salvar'}
            </button>
            {dirty && <span className="small muted" style={{ alignSelf: 'center' }}>Há alterações não salvas.</span>}
          </div>
        </fieldset>
      </form>

      {e.status === 'confirmado' && <SessionReportCard execution={e} />}
      {!readOnly && <SummaryAssistant execution={e} dirty={dirty} />}
      <AttachmentsCard execution={e} readOnly={readOnly} />

      {!readOnly && (
        <div className="card">
          <h2>Anular</h2>
          <p className="small muted">Use para registros feitos por engano. O histórico é preservado.</p>
          <ErrorAlert error={voidM.error} />
          <button
            className="btn btn-danger"
            onClick={() => {
              const reason = window.prompt('Motivo da anulação:');
              if (reason) voidM.mutate(reason);
            }}
          >
            Anular execução
          </button>
        </div>
      )}

      {confirmDialog && (
        <Dialog title="Conferir antes de confirmar" onClose={() => setConfirmDialog(false)}>
          <dl className="props">
            <dt>Cliente</dt>
            <dd>
              <strong>{e.clientName ?? '—'}</strong>
            </dd>
            <dt>Projeto</dt>
            <dd>
              <strong>{e.projectName ?? '—'}</strong>
            </dd>
            <dt>Contrato</dt>
            <dd>{e.contractCode ? `${e.contractCode} — ${MODALITY_LABEL[e.contractModality!]}` : '—'}</dd>
            <dt>Executor</dt>
            <dd>
              <strong>{executorName ?? e.executorName}</strong>
            </dd>
            <dt>Data</dt>
            <dd>{date(e.serviceDate)}</dd>
            <dt>Duração</dt>
            <dd>
              {hm(e.durationMinutes)} {e.modality && `· ${SERVICE_MODALITY_LABEL[e.modality]}`}
            </dd>
          </dl>
          <p className="small muted" style={{ marginTop: '0.75rem' }}>
            {e.contractModality === 'banco_horas' && 'Confirmar consome as horas do banco (o que passar do saldo fica como excedente pendente, sem cobrança automática).'}
            {e.contractModality === 'avulso' && 'Confirmar gera um item faturável (horas × valor da hora) para conferência.'}
            {e.contractModality === 'mensalidade' && 'Mensalidade: confirmar registra a produção, sem cobrança por sessão.'}
            {e.contractModality === 'valor_fechado' && 'Valor fechado: confirmar registra o esforço, sem cobrança por hora.'} Nenhuma mensagem é enviada ao cliente.
          </p>
          {sharedSession && (
            <div className="alert alert-warning">
              <strong>Sessão com mais de um consultor</strong>
              {sharedSession.others.map((o) => o.executor).join(', ')} já registrou esta sessão com cobertura do cliente. Como esta execução deve contar?
              <div className="actions" style={{ marginTop: '0.5rem' }}>
                <button className="btn btn-sm btn-primary" disabled={transition.isPending} onClick={() => transition.mutate({ to: 'confirmado', sharedSessionCoverage: 'somente_producao' })}>
                  Só produção (não consumir/cobrar de novo)
                </button>
                <button className="btn btn-sm" disabled={transition.isPending} onClick={() => transition.mutate({ to: 'confirmado', sharedSessionCoverage: 'consumir' })}>
                  Também consumir/cobrar do cliente
                </button>
              </div>
            </div>
          )}
          <ErrorAlert error={transition.error instanceof ApiError && transition.error.code === 'shared_session_coverage_required' ? null : transition.error} title="Ainda não é possível confirmar." />
          <div className="actions" style={{ marginTop: '0.75rem' }}>
            <button className="btn btn-primary" disabled={transition.isPending} onClick={() => transition.mutate('confirmado')}>
              Confirmar execução
            </button>
            <button className="btn" onClick={() => setConfirmDialog(false)}>
              Voltar
            </button>
          </div>
        </Dialog>
      )}
      {readOnly && (
        <p>
          <button className="btn" onClick={() => navigate('/execucoes')}>
            Voltar
          </button>
        </p>
      )}
    </>
  );
}

// ------------------------------------------------------------------ resumo assistido (seção 8.4)

const SOURCE_LABEL: Record<SourceKind, string> = { digitado: 'Digitar', colado: 'Colar texto', txt: 'Arquivo TXT', srt: 'Legenda SRT', vtt: 'Legenda VTT' };

function SummaryAssistant({ execution: e, dirty }: { execution: ExecutionDetail; dirty: boolean }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<SourceKind>(e.source?.kind ?? 'colado');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [editingSource, setEditingSource] = useState(!e.source);
  const [suggestion, setSuggestion] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const latest: SummaryRun | undefined = e.summaryRuns[0];
  useEffect(() => {
    if (latest?.status === 'gerado') setSuggestion(latest.suggestedText ?? '');
  }, [latest?.id, latest?.status, latest?.suggestedText]);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['execution', e.id] });

  const original = useQuery({
    queryKey: ['execution-source', e.id, e.source?.textVersion],
    queryFn: () => api.get<{ originalText: string }>(`/executions/${e.id}/source/original`),
    enabled: Boolean(e.source) && editingSource,
  });
  useEffect(() => {
    if (editingSource && original.data && !text) setText(original.data.originalText);
  }, [original.data, editingSource, text]);

  const saveSource = useMutation({
    mutationFn: () => api.put(`/executions/${e.id}/source`, { kind, fileName, text, expectedVersion: e.source?.textVersion ?? null }),
    onSuccess: () => {
      setEditingSource(false);
      refresh();
    },
  });
  const generate = useMutation({ mutationFn: () => api.post<SummaryRun>(`/executions/${e.id}/summary-runs`), onSettled: refresh });
  const apply = useMutation({
    mutationFn: (opts: { replaceExisting?: boolean; acceptStale?: boolean }) =>
      api.post(`/executions/${e.id}/summary-runs/${latest!.id}/apply`, { text: suggestion, executionVersion: e.version, ...opts }),
    onSuccess: () => {
      refresh();
      void qc.invalidateQueries({ queryKey: ['executions'] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'summary_exists' && window.confirm('Já existe uma descrição no registro. Substituir pela sugestão revisada?')) apply.mutate({ replaceExisting: true, acceptStale: false });
      if (err instanceof ApiError && err.code === 'stale_summary' && window.confirm('A fonte mudou depois desta sugestão. Aplicar mesmo assim?')) apply.mutate({ replaceExisting: true, acceptStale: true });
    },
  });
  const reject = useMutation({ mutationFn: () => api.post(`/executions/${e.id}/summary-runs/${latest!.id}/reject`), onSuccess: refresh });

  async function onFile(f: File | undefined) {
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (ext !== 'txt' && ext !== 'srt' && ext !== 'vtt') return alert('Use arquivo .txt, .srt ou .vtt.');
    if (f.size > 1_000_000) return alert('Arquivo maior que 1 MB. Divida a transcrição.');
    setKind(ext);
    setFileName(f.name);
    setText(await f.text());
  }

  const applyErr = apply.error instanceof ApiError && (apply.error.code === 'summary_exists' || apply.error.code === 'stale_summary') ? null : apply.error;

  return (
    <section className="card">
      <h2>Resumo assistido</h2>
      <p className="small muted">
        A descrição ou transcrição é material <strong>interno</strong>: nunca vai ao cliente. Só o resumo revisado e aplicado entra no registro. Gerar ou aplicar não
        confirma a execução, não altera horas nem cobrança e não envia mensagens.
      </p>

      <h3>1. Fonte</h3>
      {e.source && !editingSource ? (
        <>
          <div className="small muted">
            {SOURCE_LABEL[e.source.kind]}
            {e.source.fileName && ` · ${e.source.fileName}`} · versão {e.source.textVersion}
          </div>
          <pre className="source-preview">{e.source.normalizedText}</pre>
          <button className="btn btn-sm" onClick={() => setEditingSource(true)}>
            Alterar fonte
          </button>
        </>
      ) : (
        <div className="form">
          <div className="segmented" role="group" aria-label="Tipo de fonte">
            {(['digitado', 'colado'] as SourceKind[]).map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => (setKind(k), setFileName(null))}>
                {SOURCE_LABEL[k]}
              </button>
            ))}
            <button type="button" aria-pressed={['txt', 'srt', 'vtt'].includes(kind)} onClick={() => fileRef.current?.click()}>
              Importar arquivo
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".txt,.srt,.vtt,text/plain" hidden onChange={(ev) => void onFile(ev.target.files?.[0])} />
          {fileName && <span className="small muted">Arquivo: {fileName} ({SOURCE_LABEL[kind]}). O original é preservado; marcas de tempo são normalizadas para leitura.</span>}
          <label className="visually-hidden" htmlFor="src-text">Descrição ou transcrição</label>
          <textarea id="src-text" rows={8} value={text} onChange={(ev) => setText(ev.target.value)} placeholder="Descreva o atendimento ou cole a transcrição do aplicativo de gravação." />
          <ErrorAlert error={saveSource.error} />
          <div className="actions">
            <button className="btn btn-primary btn-sm" disabled={!text.trim() || saveSource.isPending} onClick={() => saveSource.mutate()}>
              Salvar fonte
            </button>
            {e.source && (
              <button className="btn btn-sm" onClick={() => (setEditingSource(false), setText(''))}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      <h3 style={{ marginTop: '1rem' }}>2. Sugestão</h3>
      {!e.ai.configured ? (
        <div className="alert alert-info">
          <strong>Geração por IA não configurada</strong>
          Um administrador pode ativar em Configurações › Integrações (chave da Anthropic). Enquanto isso, preencha a descrição do conteúdo manualmente; a fonte fica guardada.
        </div>
      ) : (
        <>
          {e.ai.simulator && <div className="alert alert-warning small">Ambiente de teste: as sugestões vêm de um SIMULADOR, não de IA real.</div>}
          <ErrorAlert error={generate.error} />
          <button className="btn" disabled={!e.source || editingSource || generate.isPending || latest?.status === 'processando' || dirty} onClick={() => generate.mutate()}>
            {generate.isPending || latest?.status === 'processando' ? 'Processando…' : latest ? 'Gerar nova sugestão' : 'Gerar resumo com IA'}
          </button>
          {dirty && <span className="small muted"> Salve o registro antes.</span>}
        </>
      )}

      {latest?.status === 'falhou' && <div className="alert alert-error" style={{ marginTop: '0.75rem' }}>A geração falhou ({latest.error}). Nada foi perdido; tente de novo ou preencha manualmente.</div>}
      {latest?.status === 'gerado' && (
        <div className="form" style={{ marginTop: '0.75rem' }}>
          {latest.stale && <div className="alert alert-warning">A fonte mudou depois desta sugestão: ela pode estar desatualizada.</div>}
          {latest.uncertainPoints && latest.uncertainPoints.length > 0 && (
            <div className="alert alert-warning">
              <strong>Pontos incertos — confira antes de usar</strong>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {latest.uncertainPoints.map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
            </div>
          )}
          <label htmlFor="suggestion" style={{ fontWeight: 600 }}>
            Revise e edite os tópicos
          </label>
          <textarea id="suggestion" rows={8} value={suggestion} onChange={(ev) => setSuggestion(ev.target.value)} />
          <ErrorAlert error={applyErr ?? reject.error} />
          <div className="actions">
            <button className="btn btn-primary" disabled={!suggestion.trim() || apply.isPending || dirty} onClick={() => apply.mutate({})}>
              Aplicar ao registro
            </button>
            <button className="btn" disabled={reject.isPending} onClick={() => reject.mutate()}>
              Recusar sugestão
            </button>
          </div>
        </div>
      )}
      {e.summaryRuns.length > 0 && (
        <details style={{ marginTop: '0.75rem' }}>
          <summary className="small">Histórico de sugestões ({e.summaryRuns.length})</summary>
          <ul className="small">
            {e.summaryRuns.map((r) => (
              <li key={r.id}>
                {dateTime(r.createdAt)} · {r.status} · fonte v{r.sourceVersion} · {r.provider}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ anexos

function AttachmentsCard({ execution: e, readOnly }: { execution: ExecutionDetail; readOnly: boolean }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['execution', e.id] });

  async function upload(f: File | undefined) {
    if (!f) return;
    setError(null);
    if (f.size > 10 * 1024 * 1024) return setError(new Error('Arquivo maior que 10 MB.'));
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const res = await fetch(`/api/v1/executions/${e.id}/attachments`, { method: 'POST', body: fd, credentials: 'same-origin' });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new ApiError(res.status, b?.error?.code ?? 'error', b?.error?.message ?? 'Falha no envio.');
      }
      refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'network_error', 'Sem conexão: o anexo não foi enviado.'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Remover este anexo?')) return;
    try {
      await api.del(`/attachments/${id}`);
      refresh();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>Anexos</h2>
        {!readOnly && (
          <>
            <button className="btn btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? 'Enviando…' : 'Adicionar anexo'}
            </button>
            <input ref={fileRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.txt,.csv" onChange={(ev) => void upload(ev.target.files?.[0])} />
          </>
        )}
      </div>
      <p className="small muted">PDF, imagens, DOCX, XLSX, TXT ou CSV até 10 MB. Arquivos privados: só usuários conectados baixam.</p>
      <ErrorAlert error={error} />
      {e.attachments.length === 0 ? (
        <Empty>Nenhum anexo.</Empty>
      ) : (
        <div className="list">
          {e.attachments.map((a) => (
            <div key={a.id} className="list-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center' }}>
              <a href={`/api/v1/attachments/${a.id}/download`}>{a.fileName}</a>
              <span className="small muted">{(a.sizeBytes / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} KB</span>
              {!readOnly && (
                <button className="btn btn-ghost btn-sm" onClick={() => void remove(a.id)} aria-label={`Remover ${a.fileName}`}>
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ cobertura aplicada (seção 9)

const OVERAGE_LABEL = { nenhum: '', pendente: 'aguardando decisão', cobrado_avulso: 'faturado como avulso', sem_cobranca: 'sem cobrança', coberto_credito: 'descontado de banco de horas' } as const;

function CoveragePanel({ execution: e }: { execution: ExecutionDetail }) {
  const item = e.billingItems.find((i) => i.sourceType === 'execution' && i.billingStatus !== 'cancelado');
  return (
    <div className={`alert ${e.overageStatus === 'pendente' ? 'alert-warning' : 'alert-success'}`}>
      <strong>Confirmada em {dateTime(e.confirmedAt)}. Nenhuma mensagem foi enviada ao cliente.</strong>
      {e.coverageMode === 'somente_producao' && <div>Sessão compartilhada: registrada só como produção deste consultor, sem novo consumo ou cobrança ao cliente.</div>}
      {e.coverageMode === 'padrao' && e.billingCondition === 'coberto_banco' && (
        <div>
          Consumiu {hm(e.coveredMinutes ?? 0)} do banco {e.contractCode}. Saldo atual: {hm(e.contractBalance ?? 0)}.{' '}
          {e.contractId && <Link to={`/bancos-de-horas/${e.contractId}`}>Ver extrato</Link>}
        </div>
      )}
      {e.billingCondition === 'faturavel' && item && (
        <div>
          Item faturável: {Number(item.quantity).toLocaleString('pt-BR')} h × {money(item.unitPrice)} = <strong>{money(item.total)}</strong> (v{item.version}, {item.billingStatus === 'faturado' ? 'faturado' : 'aguardando conferência/envio'}).{' '}
          <Link to="/faturamento">Faturamento</Link>
        </div>
      )}
      {e.billingCondition === 'mensalidade' && <div>Mensalidade: produção registrada, sem cobrança por sessão.</div>}
      {e.billingCondition === 'valor_fechado' && <div>Valor fechado: esforço registrado, sem cobrança por hora.</div>}
      {e.overageMinutes > 0 && <OverageDecision execution={e} />}
    </div>
  );
}

interface OverageTarget {
  id: string;
  code: string;
  projectName: string;
  endDate: string | null;
  balance: number;
  isOwn: boolean;
}

// Excedente (seção 9): descontar de um banco de horas vigente do MESMO cliente, faturar avulso ou não cobrar.
// A decisão pode ser revista enquanto a cobrança avulsa não foi enviada ao financeiro nem faturada.
function OverageDecision({ execution: e }: { execution: ExecutionDetail }) {
  const qc = useQueryClient();
  const overItem = e.billingItems.find((i) => i.sourceType === 'overage' && i.billingStatus !== 'cancelado');
  const itemLocked = Boolean(overItem && (overItem.transportStatus !== 'nao_enviado' || overItem.billingStatus === 'faturado' || overItem.billingStatus === 'ajustado'));
  const revisable = (e.overageStatus === 'cobrado_avulso' || e.overageStatus === 'sem_cobranca') && !itemLocked;
  const [open, setOpen] = useState(false);
  const deciding = e.overageStatus === 'pendente' || open;
  const options = useQuery({
    queryKey: ['overage-options', e.id],
    queryFn: () => api.get<{ data: OverageTarget[] }>(`/executions/${e.id}/overage-options`),
    enabled: deciding,
  });
  const eligible = (options.data?.data ?? []).filter((o) => o.balance >= e.overageMinutes);
  const [target, setTarget] = useState('');
  const chosen = target || eligible.find((o) => o.isOwn)?.id || eligible[0]?.id || '';
  const avulsoValue = e.contractHourlyRate ? money(((e.overageMinutes / 60) * Number(e.contractHourlyRate)).toFixed(2)) : null;
  const decide = useMutation({
    mutationFn: (body: { decision: 'cobrar_avulso' | 'sem_cobranca' | 'consumir_credito'; targetContractId?: string }) => api.post(`/executions/${e.id}/overage`, body),
    onSuccess: () => {
      setOpen(false);
      for (const key of [['execution', e.id], ['overage-options', e.id], ['billing-items'], ['hour-banks']]) void qc.invalidateQueries({ queryKey: key });
    },
  });
  const chosenLabel = eligible.find((o) => o.id === chosen);

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <strong>Excedente: {hm(e.overageMinutes)} além do saldo</strong> — {OVERAGE_LABEL[e.overageStatus]}
      {overItem && <> · item {money(overItem.total)}{itemLocked && ' (já enviado/faturado)'}</>}
      {revisable && !open && (
        <div className="actions" style={{ marginTop: '0.4rem' }}>
          <button className="btn btn-sm" onClick={() => setOpen(true)}>
            Rever decisão
          </button>
        </div>
      )}
      {deciding && (
        <div className="form" style={{ marginTop: '0.5rem', gap: '0.5rem' }}>
          <div className="small">
            {e.overageStatus === 'pendente' ? 'Nada foi cobrado. Escolha conforme o combinado com o cliente:' : 'Ao mudar, a cobrança avulsa ainda não enviada é cancelada.'}
          </div>
          <ErrorAlert error={decide.error ?? options.error} />
          <div className="card" style={{ padding: '0.75rem', margin: 0 }}>
            <strong>Deduzir de contrato vigente deste cliente</strong>
            {options.isLoading ? (
              <Loading />
            ) : eligible.length === 0 ? (
              <div className="small muted">
                Nenhum banco de horas vigente de {e.clientName} tem saldo para {hm(e.overageMinutes)}. Registre a contratação ou adicione horas no banco e volte aqui.
              </div>
            ) : (
              <>
                <select aria-label="Contrato para deduzir" value={chosen} onChange={(ev) => setTarget(ev.target.value)} style={{ marginTop: '0.4rem' }}>
                  {eligible.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.code} · {o.projectName} · saldo {hm(o.balance)}
                      {o.isOwn ? ' (contrato desta execução)' : ''}
                    </option>
                  ))}
                </select>
                {(options.data?.data ?? []).some((o) => o.balance < e.overageMinutes) && (
                  <div className="small muted">Contratos sem saldo suficiente não aparecem.</div>
                )}
                <div className="actions" style={{ marginTop: '0.4rem' }}>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={decide.isPending || !chosen}
                    onClick={() =>
                      window.confirm(`Deduzir ${hm(e.overageMinutes)} do contrato ${chosenLabel?.code} (${chosenLabel?.projectName})?`) &&
                      decide.mutate({ decision: 'consumir_credito', targetContractId: chosen })
                    }
                  >
                    Deduzir {hm(e.overageMinutes)}
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="actions">
            {e.overageStatus !== 'cobrado_avulso' && (
              <button
                className="btn btn-sm"
                disabled={decide.isPending}
                onClick={() => window.confirm(`Faturar ${hm(e.overageMinutes)} como avulso${avulsoValue ? ` (${avulsoValue})` : ''}?`) && decide.mutate({ decision: 'cobrar_avulso' })}
              >
                Faturar avulso{avulsoValue ? ` (${avulsoValue})` : ''}
              </button>
            )}
            {e.overageStatus !== 'sem_cobranca' && (
              <button className="btn btn-sm btn-ghost" disabled={decide.isPending} onClick={() => window.confirm('Registrar o excedente sem cobrança?') && decide.mutate({ decision: 'sem_cobranca' })}>
                Não cobrar
              </button>
            )}
            {open && (
              <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}
      {e.overageStatus === 'coberto_credito' && <div className="small">Para mudar depois de descontado do banco, anule a execução (o consumo é estornado) e registre de novo.</div>}
    </div>
  );
}

// ------------------------------------------------------------------ relatório da sessão (seção 12)

function SessionReportCard({ execution: e }: { execution: ExecutionDetail }) {
  return (
    <section className="card">
      <h2>Relatório da sessão</h2>
      <p className="small muted">Documento do cliente: conteúdo, consultor, data, horas e, se houver, consumo e saldo do banco. Não inclui transcrição, anotação interna nem valores.</p>
      <div className="actions">
        <a className="btn btn-sm" href={`/api/v1/reports/session/${e.id}/pdf`} target="_blank" rel="noopener">
          Ver PDF
        </a>
        <a className="btn btn-sm" href={`/api/v1/reports/session/${e.id}/html`} target="_blank" rel="noopener">
          Ver HTML
        </a>
        <PrepareMessageButton label="Preparar envio por e-mail" className="btn btn-sm btn-primary" body={{ purpose: 'relatorio_sessao', channel: 'email', executionId: e.id }} />
        <PrepareMessageButton label="Preparar por WhatsApp" body={{ purpose: 'relatorio_sessao', channel: 'whatsapp', executionId: e.id }} />
      </div>
      <RelatedMessages filter={{ executionId: e.id }} />
    </section>
  );
}
